// Pull-to-refresh hook for vertically-scrolled lists. iOS Safari and
// most mobile sites use a proportional 360° indicator that rotates as
// the user pulls, fires the refresh at threshold, and holds the
// spinner until the async refresh resolves.
//
// Why a hook rather than a wrapper component: most lists in this app
// are deeply embedded with inline styles, and wrapping a giant subtree
// in a new container risks z-index / overflow regressions. The hook
// returns `{ pullDistance, isRefreshing, bind }` so callers can:
//   - Spread `bind` onto their scroll container.
//   - Render their own pull indicator (or use the included
//     <PullToRefreshIndicator> component, exported separately).
//   - Wire `isRefreshing` to whatever spinner UI they want.
//
// Gesture rules:
//   - Pull only registers when `scrollTop === 0` at touchStart.
//   - Vertical-only — horizontal drags are ignored.
//   - Rubber-band: pull beyond threshold gets a 40% deceleration
//     curve, so the user feels the "catch."
//   - Threshold default 70 px; firing the refresh hold the spinner
//     locked at threshold until the user's onRefresh() promise
//     resolves.
//   - Reduced motion: indicator opacity-only (no rotation).
//
// Reference: §11 #14 "Pull-to-refresh with proportional indicator.
// Indicator rotates 360° as you pull from 0 → 80px; doesn't snap-on.
// Native iOS feel."

import { useCallback, useEffect, useRef, useState } from 'react';
import useReducedMotion from './useReducedMotion';
import { feedback } from './feedback';

const DEFAULT_THRESHOLD_PX = 70;
const DEFAULT_MAX_PULL_PX = 140;
const VERTICAL_DOMINANCE_PX = 4;

export default function usePullToRefresh(onRefresh, options = {}) {
  const {
    threshold = DEFAULT_THRESHOLD_PX,
    maxPull = DEFAULT_MAX_PULL_PX,
    hapticOnFire = true,
  } = options;

  const reduceMotion = useReducedMotion();
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const startRef = useRef(null);
  const draggingRef = useRef(false);
  const onRefreshRef = useRef(onRefresh);

  useEffect(() => { onRefreshRef.current = onRefresh; }, [onRefresh]);

  const fireRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    if (hapticOnFire) feedback('tap');
    try {
      const result = onRefreshRef.current ? onRefreshRef.current() : undefined;
      if (result && typeof result.then === 'function') await result;
    } catch {
      // swallow — refresh errors should be handled by the caller via
      // its own error surface (toast, retry, etc.).
    } finally {
      setIsRefreshing(false);
      setPullDistance(0);
    }
  }, [isRefreshing, hapticOnFire]);

  const onTouchStart = useCallback((e) => {
    if (isRefreshing) return;
    const t = e.touches && e.touches[0];
    if (!t) return;
    // Only honor pull when scrolled to the very top. Check both the
    // immediate scroll container (currentTarget) AND the document/window
    // scroll, because the journal binds onto a wrapper that doesn't
    // own its own overflow — the actual scroll is on the body. Without
    // the window check the guard becomes a no-op and pull-to-refresh
    // fires mid-scroll.
    const container = e.currentTarget;
    const containerScrolled = !!(container && container.scrollTop > 0);
    const winScrolled = typeof window !== 'undefined' && (
      (window.scrollY || 0) > 0 ||
      (document.documentElement && document.documentElement.scrollTop > 0) ||
      (document.body && document.body.scrollTop > 0)
    );
    if (containerScrolled || winScrolled) return;
    startRef.current = { x: t.clientX, y: t.clientY };
    draggingRef.current = false;
  }, [isRefreshing]);

  const onTouchMove = useCallback((e) => {
    if (isRefreshing || !startRef.current) return;
    const t = e.touches && e.touches[0];
    if (!t) return;
    const dy = t.clientY - startRef.current.y;
    const dx = t.clientX - startRef.current.x;

    if (!draggingRef.current) {
      // Need a meaningful downward intent + vertical dominance.
      if (dy <= VERTICAL_DOMINANCE_PX) return;
      if (Math.abs(dx) > Math.abs(dy)) {
        startRef.current = null; // horizontal — abort
        return;
      }
      draggingRef.current = true;
    }
    if (dy <= 0) {
      setPullDistance(0);
      return;
    }
    // Rubber-band past threshold so the pull keeps feeling "alive"
    // but doesn't linearly scroll the whole screen.
    let next = dy;
    if (next > threshold) {
      const excess = next - threshold;
      next = threshold + excess * 0.4;
    }
    if (next > maxPull) next = maxPull;
    setPullDistance(next);
  }, [isRefreshing, threshold, maxPull]);

  const onTouchEnd = useCallback(() => {
    if (!draggingRef.current) {
      startRef.current = null;
      return;
    }
    const px = pullDistance;
    startRef.current = null;
    draggingRef.current = false;
    if (px >= threshold) {
      fireRefresh();
    } else {
      setPullDistance(0);
    }
  }, [pullDistance, threshold, fireRefresh]);

  const onTouchCancel = useCallback(() => {
    startRef.current = null;
    draggingRef.current = false;
    setPullDistance(0);
  }, []);

  // Indicator progress: 0 → 1 by threshold. Past threshold it stays at 1
  // (we don't keep rotating past full circle — that'd read as "still
  // pulling" which is misleading once the threshold is met).
  const progress = Math.min(1, pullDistance / threshold);

  return {
    pullDistance,
    isRefreshing,
    progress,
    reduceMotion,
    bind: {
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onTouchCancel,
    },
  };
}

// Standalone indicator — callers can render this or build their own.
// Sized to sit just above the scroll container; renders absolutely
// positioned by the caller, NOT in document flow.
//
// SVG arc with stroke-dasharray driven by progress (0–1) for the
// proportional rotation. When isRefreshing fires, the arc holds full
// and spins continuously via CSS (disabled under reduced-motion).
export function PullToRefreshIndicator({ progress, isRefreshing, reduceMotion, size = 28 }) {
  const stroke = 3;
  const r = (size / 2) - stroke;
  const circumference = 2 * Math.PI * r;
  const dashOffset = circumference * (1 - progress);
  const rotation = reduceMotion ? 0 : progress * 360;

  // When refreshing, oscillate the rotation via a setInterval-free
  // approach: a CSS transition on `transform` cycles between two angles.
  // We toggle via state at ~700 ms per half-revolution so the user
  // sees continuous motion without depending on a globally-registered
  // @keyframes (which doesn't exist in this codebase yet — A.4 polish
  // will consolidate keyframes; this fallback ensures refresh always
  // has a visible spinner).
  const [spinTick, setSpinTick] = useState(0);
  useEffect(() => {
    if (!isRefreshing || reduceMotion) return undefined;
    const id = setInterval(() => setSpinTick((t) => t + 1), 700);
    return () => clearInterval(id);
  }, [isRefreshing, reduceMotion]);

  const spinAngle = isRefreshing && !reduceMotion ? (spinTick * 360) : 0;
  const finalRotation = isRefreshing && !reduceMotion ? spinAngle - 90 : rotation - 90;

  return (
    <div
      aria-hidden={!isRefreshing}
      role={isRefreshing ? 'status' : 'presentation'}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: `${size + stroke * 2}px`,
        height: `${size + stroke * 2}px`,
        opacity: progress > 0 || isRefreshing ? 1 : 0,
        transition: 'opacity 120ms ease-out',
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{
          transform: `rotate(${finalRotation}deg)`,
          transition: isRefreshing && !reduceMotion ? 'transform 700ms linear' : 'transform 80ms ease-out',
        }}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="rgba(255,255,255,0.12)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--tg, #d4a832)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={isRefreshing ? circumference * 0.55 : dashOffset}
        />
      </svg>
    </div>
  );
}
